import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  Users,
  Folder,
  FileText,
  Settings2,
  Siren,
  HelpCircle,
  Settings,
  PlusSquare,
  UserCog,
  UserCircle,
  Lock,
  Bell,
  Key,
  Webhook,
  Share2,
  MessageSquare,
  Shield,
  ClipboardList,
  History,
  ListOrdered,
  Cloud,
  Network,
  Code,
  Building2,
  Pencil,
  Play,
  CheckCircle,
  Bug,
  Flag,
  BarChart,
  Palette,
  Sliders,
  Database,
  ChevronDown,
  Files,
  ArrowRightLeft,
  Video,
  Trash2,
} from "lucide-react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useModal } from "../../context/ModalContext";
import { useSelector, useDispatch } from "react-redux";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { projectService } from "../../services/projectService";
import { setProjectsData } from "../../redux/actions";
import { getStoredClientId } from "../../utils/tokenUtils";
import { getLastTestCaseForHistory } from "../../utils/testCaseHistoryNav";
import { useSettingsPageAccess } from "../../hooks/useCan";
import { useWorkspaceTabsOptional } from "../../hooks/useWorkspaceTabs";
import {
  resolveTabKeyFromSidebarChild,
  resolveTabKeyFromSidebarItem,
} from "../../config/workspaceTabs";
import MyAccount from "../MyAccount";

/** Nav item label, icon, and header title color */
const SIDEBAR_LABEL_COLOR = "#3D3638FF";

/** On /settings, keep Projects/Migration sidebar when this query is set */
const SETTINGS_SIDEBAR_LAYOUT_PARAM = "sidebar_layout";
const SETTINGS_SIDEBAR_LAYOUT_PROJECTS = "projects";
const ADMINISTRATION_MENU_SESSION_KEY = "sidebar:administration-menu-open";

/** Persisted sidebar chrome when visiting documentation / support / customization */
const SIDEBAR_CHROME_BUCKET_KEY = "sidebar:chrome-bucket";
const SIDEBAR_CHROME_BUCKETS = new Set([
  "projects",
  "settings",
  "clients",
  "myAccount",
  "testCases",
]);

/** Selected row in Administration dropdown */
const ADMIN_NAV_SELECTED = {
  color: "#3D3638FF",
  fontWeight: 700,
};

const Sidebar = ({ setParentColapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const workspaceTabs = useWorkspaceTabsOptional();

  /** History list: default tab vs ?tab=test_step; detail /history/:id counts as test case history. */
  const isSidebarChildPathActive = useCallback(
    (childOrPath, pathname) => {
      const child =
        typeof childOrPath === "string" ? { path: childOrPath } : childOrPath;
      const childPath = child?.path;
      if (!childPath) return false;
      if (childPath === "/test-cases/history/steps") {
        return pathname.startsWith("/test-cases/history/steps");
      }
      if (childPath === "/test-cases/history") {
        if (pathname.startsWith("/test-cases/history/steps")) {
          return child.historyTab === "test_step";
        }
        if (!pathname.startsWith("/test-cases/history")) return false;
        if (child.historyTab === "test_step") {
          return (
            pathname === "/test-cases/history" &&
            searchParams.get("tab") === "test_step"
          );
        }
        if (
          pathname === "/test-cases/history" &&
          searchParams.get("tab") === "test_step"
        ) {
          return false;
        }
        return true;
      }
      if (childPath === "/test-cases") {
        return (
          pathname.startsWith("/test-cases") &&
          !pathname.startsWith("/test-cases/history")
        );
      }
      return pathname.startsWith(childPath);
    },
    [searchParams],
  );
  const { isModalOpen } = useModal();
  const dispatch = useDispatch();

  // Get projects from Redux state - state.projects is the reducer, which has { projects: [] }
  const projectsFromState = useSelector((state) => state.projects);
  // Ensure projects is always an array
  const projects = Array.isArray(projectsFromState)
    ? projectsFromState
    : projectsFromState && Array.isArray(projectsFromState.projects)
      ? projectsFromState.projects
      : [];

  const [collapsed, setCollapsed] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [administrationMenuOpen, setAdministrationMenuOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      window.sessionStorage.getItem(ADMINISTRATION_MENU_SESSION_KEY) === "1"
    );
  });

  const persistAdministrationMenuSession = useCallback((isOpen) => {
    if (typeof window === "undefined") return;
    if (isOpen) {
      window.sessionStorage.setItem(ADMINISTRATION_MENU_SESSION_KEY, "1");
    } else {
      window.sessionStorage.removeItem(ADMINISTRATION_MENU_SESSION_KEY);
    }
  }, []);

  const setAdministrationMenuOpenPersisted = useCallback(
    (valueOrUpdater) => {
      setAdministrationMenuOpen((prev) => {
        const next =
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(prev)
            : valueOrUpdater;
        persistAdministrationMenuSession(next);
        return next;
      });
    },
    [persistAdministrationMenuSession],
  );

  useEffect(() => {
    setParentColapsed(collapsed);
  }, [collapsed]);

  useEffect(() => {
    /** Only collapse the Administration submenu when user actually hides the sidebar; do not reset on Sidebar remount while expanded (RouteLoader flashes between routes). */
    if (collapsed) {
      setAdministrationMenuOpenPersisted(false);
    }
  }, [collapsed, setAdministrationMenuOpenPersisted]);

  useEffect(() => {
    if (!location.pathname.startsWith("/settings")) {
      setAdministrationMenuOpenPersisted(false);
    }
  }, [location.pathname, setAdministrationMenuOpenPersisted]);

  useEffect(() => {
    if (
      location.pathname.startsWith("/settings") &&
      searchParams.get(SETTINGS_SIDEBAR_LAYOUT_PARAM) ===
        SETTINGS_SIDEBAR_LAYOUT_PROJECTS &&
      window.sessionStorage.getItem(ADMINISTRATION_MENU_SESSION_KEY) === "1"
    ) {
      setAdministrationMenuOpenPersisted(true);
    }
  }, [
    location.pathname,
    location.search,
    searchParams,
    setAdministrationMenuOpenPersisted,
  ]);

  // Load projects into Redux
  useEffect(() => {
    const loadProjects = async () => {
      if (!user) return;

      try {
        const urlClientId = searchParams.get("client_id");
        /** Chief-admin flows from All Clients (Edit / System Configuration) pass client_id in URL but must not change sidebar scope */
        const fromAllClientsFlow = searchParams.get("from") === "all-clients";
        const effectiveClientId = fromAllClientsFlow
          ? getStoredClientId() || user?.client_id || null
          : urlClientId || user?.client_id || null;

        if (!effectiveClientId) return;

        let allProjects = [];
        let currentPage = 1;
        const limit = 100; // Backend max limit is 100
        let hasMore = true;

        console.log(
          "Sidebar: Loading projects with limit:",
          limit,
          "for client:",
          effectiveClientId,
        );

        // Load all pages of projects
        while (hasMore) {
          const response = await projectService.getProjects({
            client_id: effectiveClientId,
            limit: limit,
            page: currentPage,
          });

          console.log(
            "Sidebar: API response for page",
            currentPage,
            ":",
            response,
          );

          // Handle response structure: response.data.projects or response.projects
          let projectsList = [];
          if (
            response?.data?.projects &&
            Array.isArray(response.data.projects)
          ) {
            projectsList = response.data.projects;
          } else if (response?.projects && Array.isArray(response.projects)) {
            projectsList = response.projects;
          } else if (Array.isArray(response?.data)) {
            projectsList = response.data;
          } else if (Array.isArray(response)) {
            projectsList = response;
          }

          console.log(
            "Sidebar: Extracted projects list length:",
            projectsList.length,
          );

          if (projectsList.length > 0) {
            allProjects = [...allProjects, ...projectsList];
          }

          // Check if there are more pages
          const pagination = response?.data?.pagination || response?.pagination;
          if (pagination) {
            console.log("Sidebar: Pagination info:", pagination);
            hasMore = currentPage < pagination.totalPages;
            currentPage++;
          } else {
            // If no pagination info, stop if we got fewer than limit projects
            hasMore = projectsList.length === limit;
            currentPage++;
          }
        }

        console.log("Sidebar: Total projects loaded:", allProjects.length);

        if (allProjects.length > 0) {
          dispatch(setProjectsData(allProjects));
        }
      } catch (error) {
        console.error("Error loading projects in Sidebar:", error);
      }
    };

    loadProjects();
  }, [user, searchParams, dispatch]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 1200);
    };
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("sidebarEvent", { detail: { collapsed: collapsed } }),
    );
  }, [collapsed]);
  useEffect(() => setCollapsed(isSmallScreen), [isSmallScreen]);

  // Listen for sidebar collapse events (e.g., when test case modal opens)
  useEffect(() => {
    const handleSidebarCollapse = (event) => {
      if (event.detail && typeof event.detail.collapsed === "boolean") {
        setCollapsed(event.detail.collapsed);
      }
    };

    // Check sessionStorage on mount
    const shouldCollapse =
      sessionStorage.getItem("sidebarCollapsed") === "true";
    if (shouldCollapse && !isSmallScreen) {
      setCollapsed(true);
    }

    // Listen for collapse events
    window.addEventListener("sidebarCollapse", handleSidebarCollapse);

    return () => {
      window.removeEventListener("sidebarCollapse", handleSidebarCollapse);
    };
  }, [isSmallScreen]);

  // ✅ Smart navigation context tracker (per tab)
  useEffect(() => {
    const path = location.pathname;
    const fullURL = path + location.search;

    // User on clients
    if (path.startsWith("/clients")) {
      sessionStorage.setItem("navOrigin", "clients");
      sessionStorage.removeItem("projectsFullURL");
    }

    // User on projects
    else if (path.startsWith("/projects")) {
      sessionStorage.setItem("navOrigin", "projects");
      sessionStorage.setItem("projectsFullURL", fullURL);
    }
  }, [location.pathname, location.search]);

  const handleCollapseToggle = () => setCollapsed(!collapsed);

  const toggleMenuExpansion = (label) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const drawerWidth = collapsed ? 55 : 210;
  const isActive = (itemOrPath) => {
    if (typeof itemOrPath === "string") {
      const basePath = itemOrPath.split("?")[0];
      return location.pathname === basePath;
    }
    const item = itemOrPath;
    if (item.suppressActiveHighlight) return false;
    const basePath = String(item.path).split("?")[0];
    return (
      location.pathname === basePath ||
      item.additionalPaths?.includes(location.pathname)
    );
  };

  // Helper function to check if user is chief-admin
  const isChiefAdmin = () => {
    const userRole = user?.role_name || user?.role;
    if (!userRole) return false;
    const normalizedRole = userRole.toLowerCase().replace(/[_\s-]/g, "-");
    return normalizedRole === "chief-admin";
  };

  const navigateToClientSettingsSection = useCallback(
    (section) => {
      const userRole = user?.role_name || user?.role;
      const normalizeRole = (role) =>
        role ? role.toLowerCase().replace(/[_\s-]/g, "-") : null;
      const normalizedUserRole = normalizeRole(userRole);
      const isUserChiefAdmin = normalizedUserRole === "chief-admin";

      const currentParams = new URLSearchParams(location.search);
      let clientId = currentParams.get("client_id");

      if (!clientId) {
        const storedClientId = getStoredClientId();
        clientId = storedClientId || user?.client_id || "";
      }

      if (!isUserChiefAdmin && user?.client_id) {
        clientId = user.client_id;
      }

      const path = location.pathname;
      const alreadyOnSettings = path.startsWith("/settings");
      const keepProjectsSidebarLayout =
        path === "/projects" ||
        path.startsWith("/projects/") ||
        path.startsWith("/create-project") ||
        path.startsWith("/migration") ||
        path.startsWith("/dashboard") ||
        path.startsWith("/ai-assistant") ||
        new URLSearchParams(location.search).get(
          SETTINGS_SIDEBAR_LAYOUT_PARAM,
        ) === SETTINGS_SIDEBAR_LAYOUT_PROJECTS;

      /** Keep client_id when coming from Projects/Migration — do not leak project_* into settings */
      const next = alreadyOnSettings
        ? new URLSearchParams(location.search)
        : new URLSearchParams();
      next.set("client_id", clientId ?? "");
      next.set("section", section);
      if (keepProjectsSidebarLayout) {
        next.set(
          SETTINGS_SIDEBAR_LAYOUT_PARAM,
          SETTINGS_SIDEBAR_LAYOUT_PROJECTS,
        );
      }
      navigate(`/settings?${next.toString()}`, { replace: false });
    },
    [location.pathname, location.search, navigate, user],
  );

  const updateSearchParam = (data) => {
    console.log({ data });
    // Parse existing search params
    const searchParams = new URLSearchParams(location.search);

    // Update one param
    data.forEach(({ key, value }) => {
      if (value === null || value === undefined || value === "") {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });

    // Navigate with updated params
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  // ===== MENUS =====
  // Client menu items - only visible to chief-admin (isChiefAdmin function already declared above)
  const clientMenuTop = isChiefAdmin()
    ? [
        {
          label: "All Clients",
          icon: Building2,
          path: "/clients",
          additionalPaths: ["/clients/create"],
        },
        { label: "User Management", icon: UserCog, path: "/users" },
        { label: "Content Management", icon: Folder, path: "/clients/content" },
        {
          label: "Client Dashboard",
          icon: LayoutDashboard,
          path: "/clients/dashboard",
        },
      ]
    : [];

  // Common bottom menu items (always shown except on Settings page)
  const commonBottomMenu = [
    {
      label: "Support",
      icon: HelpCircle,
      path: "/support",
      captionBelow: "Powered By TestNimbus",
    },
  ];

  const clientMenuBottom = [...commonBottomMenu];

  const { canAccess: canAccessSettingsNav, loading: settingsNavLoading } =
    useSettingsPageAccess();
  const showSettingsNav =
    isChiefAdmin() || settingsNavLoading || canAccessSettingsNav;

  /** Client Settings page sections — shared by Settings layout and Projects > Administration */
  const settingsMenu = [
    {
      id: "details",
      label: "Client Details",
      icon: Building2,
      path: "/settings",
    },
    {
      id: "general",
      label: "General Settings",
      icon: Settings2,
      path: "/settings",
    },
    { id: "users", label: "Users", icon: Users, path: "/settings" },
    { id: "roles", label: "Roles", icon: Shield, path: "/settings" },
    {
      id: "projects",
      label: "Projects & Memberships",
      icon: ClipboardList,
      path: "/settings",
    },

    { id: "audit", label: "Audit Logs", icon: History, path: "/settings" },
    { id: "trash", label: "Trash", icon: Trash2, path: "/settings" },
    // { id: "webhook", label: "Webhook", icon: Webhook, path: "/settings" },
    // { id: "api_key", label: "API Key", icon: Key, path: "/settings" },
    // {
    //   id: "security",
    //   label: "Security & SSO",
    //   icon: Settings,
    //   path: "/settings",
    // },
    // {
    //   id: "integrations",
    //   label: "Integrations",
    //   icon: Cloud,
    //   path: "/settings",
    // },

    // { id: "network", label: "Network / IP", icon: Network, path: "/settings" },
    // { id: "alerts", label: "Alerts", icon: Siren, path: "/settings" },
    // { id: "api", label: "API & Automation", icon: Code, path: "/settings" },

    // {
    //   id: "templates",
    //   label: "Test Case / Test Run Layout",
    //   icon: FileText,
    //   path: "/settings",
    // },
    // { id: "fields", label: "Fields", icon: Pencil, path: "/settings" },
    // {id:"customization", label: "Customization", icon: Sliders, path: "/settings/customization" },
    // { id: "notifications", label: "Notifications", icon: Bell, path: "/settings" },
  ];

  const projectMenuTop = [
    ...(isChiefAdmin()
      ? [
          {
            label: "All Clients",
            icon: Building2,
            path: "/clients",
            additionalPaths: ["/clients/create"],
          },
        ]
      : []),
    { label: "All Projects", icon: Folder, path: "/projects" },
    {
      label: "Integration",
      icon: Cloud,
      path: "/settings",
      settingsSection: "integrations",
    },
    { label: "Overall Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { label: "Migrate", icon: ArrowRightLeft, path: "/migration" },
    {
      label: "AI Assistant",
      icon: MessageSquare,
      path: "/ai-assistant",
      navLabelColor: "var(--color-error)",
      navIconColor: "var(--color-error)",
    },
    ...(showSettingsNav
      ? [
          {
            label: "Administration",
            icon: Settings,
            suppressActiveHighlight: true,
            children: settingsMenu,
            navPresentation: "dropdown",
          },
        ]
      : []),
  ];

  const projectMenuBottom = [...commonBottomMenu];

  const myAccountTop = [
    { label: "My Profile", icon: UserCircle, path: "/my-account" },
    { label: "Appearance", icon: Palette, path: "/my-account/appearance" },
  ];

  const myAccountBottom = [...commonBottomMenu];

  // Get current project structure from URL params
  const currentProjectId = searchParams.get("project_id");
  const currentProject = projects.find(
    (p) => p.project_id === currentProjectId,
  );
  const projectStructure = currentProject?.structure;
  const projectType = currentProject?.project_type || currentProject?.type;

  // Check if project is Multi Suite type
  const isMultiSuite =
    projectType === "MULTI_SUITE" ||
    projectType === "MULTIPLE_TEST_SUITES" ||
    projectType === "Multiple Test Suites to manage cases" ||
    projectStructure === "MULTI_SUITE" ||
    projectStructure === "MULTIPLE_TEST_SUITES";

  // Check current page (must be defined before shouldShowTestCases)
  const onSuitesPage = location.pathname.startsWith("/suites");
  const onBaselinesPage = location.pathname.startsWith("/baselines");

  // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out
  // const isBaselineEnabled =
  //   projectStructure === 'BASELINE_ENABLED' ||
  //   projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH';

  // Check if a suite is selected (for Multi Suite projects)
  const suiteId = searchParams.get("suite_id");
  const hasSelectedSuite = !!suiteId;

  // For Multi Suite projects:
  // - Hide Test Cases when on Suites page
  // - Show Test Cases only when a suite is selected (suite_id in URL)
  // For other projects: Always show Test Cases
  const shouldShowTestCases = isMultiSuite
    ? !onSuitesPage && hasSelectedSuite
    : true;

  // Main navigation menu items in exact order from design
  const mainNavMenu = [
    { label: "All Projects", icon: PlusSquare, path: "/projects" },
    { label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
    { label: "To-Do", icon: CheckCircle, path: "/todo" },
    // Only show Test Cases if conditions are met
    ...(shouldShowTestCases
      ? [
          {
            label: "Test Cases",
            icon: FileText,
            children: [
              { label: "All Test", icon: Files, path: "/test-cases" },
              { label: "Shared Steps", icon: Share2, path: "/shared-steps" },
              {
                label: "Screen Recorder",
                icon: Video,
                path: "/screen-recorder",
              },
              { label: "History", icon: History, path: "/test-cases/history" },
              {
                label: "Test step history",
                icon: ListOrdered,
                path: "/test-cases/history",
                historyTab: "test_step",
              },
            ],
          },
        ]
      : []),
    // Add Suites menu item for Multi Suite projects
    ...(isMultiSuite
      ? [{ label: "Suites", icon: Folder, path: "/suites" }]
      : []),
    { label: "AI Assistant", icon: MessageSquare, path: "/ai-assistant" },
    { label: "Test Runs & Result", icon: Play, path: "/test-runs" },
    { label: "Test Plans", icon: ClipboardList, path: "/test-plans" },
    { label: "Defects", icon: Bug, path: "/defects" },
    { label: "Milestone", icon: Flag, path: "/milestone" },
    { label: "Reports", icon: BarChart, path: "/reports" },
  ];

  const testCasesMenu = [
    {
      label: "Projects",
      type: "dropdown",
      onClick: (project_id, project_name) => {
        updateSearchParam([
          { key: "project_id", value: project_id },
          { key: "project_name", value: project_name },
        ]);
      },
    },
    ...mainNavMenu,
    {
      label: "Data Management",
      icon: Database,
      path: "/data-management",
    },
    {
      label: "Project Settings",
      icon: Settings,
      path: "/project-settings",
    },
    // SINGLE REPOSITORY WITH BASELINE SUPPORT - commented out: Baselines & Branches menu items
    // ...(projectStructure === 'BASELINE_ENABLED' || projectStructure === 'SINGLE_REPOSITORY_WITH_BASELINE_AND_BRANCH' ? [
    //   { label: "Baselines", icon: Layers, path: "/baselines" },
    //   { label: "Branches", icon: GitBranch, path: "/branches" }
    // ] : []),
  ];

  const testCasesBottomMenu = useMemo(
    () => [
      ...(showSettingsNav
        ? [{ label: "Settings", icon: Settings, path: "/settings" }]
        : []),
      {
        label: "Support",
        icon: HelpCircle,
        path: "/support",
        captionBelow: "Powered By TestNimbus",
      },
    ],
    [showSettingsNav],
  );

  const onClientsPage = location.pathname.startsWith("/clients");
  const onProjectsPage =
    location.pathname.startsWith("/projects") ||
    location.pathname.startsWith("/create-project");
  const onMyAccountPage = location.pathname.startsWith("/my-account");
  const onTestCasesPage = location.pathname.startsWith("/test-cases");
  const onTestRunsPage = location.pathname.startsWith("/test-runs");
  const onTestPlansPage = location.pathname.startsWith("/test-plans");
  const onReviewTestCasesPage =
    location.pathname.startsWith("/review-test-cases");
  const onSharedStepsPage = location.pathname.startsWith("/shared-steps");
  const onScreenRecorderPage = location.pathname.startsWith("/screen-recorder");
  // onSuitesPage and onBaselinesPage are already defined above
  const onBranchesPage = location.pathname.startsWith("/branches");
  const onUsersPage = location.pathname.startsWith("/users");
  const onSettingsPage = location.pathname.startsWith("/settings");
  const onProjectSettingsPage =
    location.pathname.startsWith("/project-settings");
  const onDataManagementPage = location.pathname.startsWith("/data-management");
  const onMigrationPage = location.pathname.startsWith("/migration");
  const onDashboardPage = location.pathname.startsWith("/dashboard");
  const onDefectsPage = location.pathname.startsWith("/defects");
  const onAIAssistantPage = location.pathname.startsWith("/ai-assistant");

  const onDocumentationPage = location.pathname.startsWith("/documentation");
  const onSupportPage = location.pathname.startsWith("/support");
  const onCustomizationPage = location.pathname.startsWith("/customization");
  const auxiliaryNavPage =
    onDocumentationPage || onSupportPage || onCustomizationPage;

  // Check if we're in a project context (test cases, test runs, test plans, etc.). Migration is client-level, not project-level.
  const inProjectContext =
    onTestCasesPage ||
    onTestRunsPage ||
    onTestPlansPage ||
    onReviewTestCasesPage ||
    onSharedStepsPage ||
    onScreenRecorderPage ||
    onSuitesPage ||
    onBaselinesPage ||
    onBranchesPage ||
    onProjectSettingsPage ||
    onDataManagementPage ||
    onDefectsPage ||
    onAIAssistantPage;

  const useProjectsChromeOnSettingsPage =
    onSettingsPage &&
    searchParams.get(SETTINGS_SIDEBAR_LAYOUT_PARAM) ===
      SETTINGS_SIDEBAR_LAYOUT_PROJECTS;

  const testCasesMenuTopFiltered = testCasesMenu.filter(
    (item) => item.type !== "dropdown" || !collapsed,
  );

  let sidebarChromeBucketLive = null;
  if (
    useProjectsChromeOnSettingsPage ||
    onProjectsPage ||
    onMigrationPage ||
    onDashboardPage ||
    onAIAssistantPage
  ) {
    sidebarChromeBucketLive = "projects";
  } else if (onSettingsPage) {
    sidebarChromeBucketLive = "settings";
  } else if (onClientsPage || onUsersPage) {
    sidebarChromeBucketLive = "clients";
  } else if (onMyAccountPage) {
    sidebarChromeBucketLive = "myAccount";
  } else if (inProjectContext) {
    sidebarChromeBucketLive = "testCases";
  }

  useEffect(() => {
    if (auxiliaryNavPage) return;
    if (!sidebarChromeBucketLive || typeof window === "undefined") return;
    sessionStorage.setItem(SIDEBAR_CHROME_BUCKET_KEY, sidebarChromeBucketLive);
  }, [auxiliaryNavPage, sidebarChromeBucketLive]);

  const persistedSidebarChromeBucket =
    auxiliaryNavPage && typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(SIDEBAR_CHROME_BUCKET_KEY)
      : null;

  const auxiliaryResolvedChrome = auxiliaryNavPage
    ? persistedSidebarChromeBucket &&
      SIDEBAR_CHROME_BUCKETS.has(persistedSidebarChromeBucket)
      ? persistedSidebarChromeBucket
      : "projects"
    : null;

  const auxiliaryTopMenus = {
    projects: projectMenuTop,
    settings: settingsMenu,
    clients: clientMenuTop,
    myAccount: myAccountTop,
    testCases: testCasesMenuTopFiltered,
  };

  const auxiliaryBottomMenus = {
    projects: projectMenuBottom,
    settings: commonBottomMenu,
    clients: clientMenuBottom,
    myAccount: myAccountBottom,
    testCases: testCasesBottomMenu,
  };

  const topMenu = auxiliaryResolvedChrome
    ? (auxiliaryTopMenus[auxiliaryResolvedChrome] ?? projectMenuTop)
    : useProjectsChromeOnSettingsPage
      ? projectMenuTop
      : onSettingsPage
        ? settingsMenu
        : onClientsPage || onUsersPage
          ? clientMenuTop
          : onProjectsPage ||
              onMigrationPage ||
              onDashboardPage ||
              onAIAssistantPage
            ? projectMenuTop
            : onMyAccountPage
              ? myAccountTop
              : inProjectContext
                ? testCasesMenuTopFiltered
                : [];

  const bottomMenu = auxiliaryResolvedChrome
    ? (auxiliaryBottomMenus[auxiliaryResolvedChrome] ?? projectMenuBottom)
    : useProjectsChromeOnSettingsPage
      ? projectMenuBottom
      : onSettingsPage
        ? commonBottomMenu // Show common bottom menu on Settings page
        : onClientsPage || onUsersPage
          ? clientMenuBottom
          : onProjectsPage || onDashboardPage || onAIAssistantPage
            ? projectMenuBottom
            : onMyAccountPage
              ? myAccountBottom
              : inProjectContext
                ? testCasesBottomMenu
                : commonBottomMenu; // Default to common bottom menu for other routes

  // Auto-expand accordion parents for the active route. Rebuild from scratch so
  // leaving Settings / Projects never leaves stale expansion or duplicate-key DOM.
  useEffect(() => {
    const nextExpanded = {};
    topMenu.forEach((item) => {
      if (item.navPresentation === "dropdown") return;
      if (item.children && item.children.length > 0) {
        const isAnyChildActive = item.children.some((child) => {
          if (child.id != null && child.path.startsWith("/settings")) {
            const section = searchParams.get("section");
            return (
              location.pathname.startsWith("/settings") &&
              (section === child.id || (!section && child.id === "details"))
            );
          }
          return (
            child.path && isSidebarChildPathActive(child, location.pathname)
          );
        });
        if (isAnyChildActive) {
          nextExpanded[item.label] = true;
        }
      }
    });
    setExpandedMenus(nextExpanded);
    // topMenu is intentionally omitted: it is a new [] each render; we only need to
    // re-sync when the route (or active history tab) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- topMenu identity is unstable
  }, [location.pathname, searchParams, isSidebarChildPathActive]);

  // ===== HEADER + SMART BACK BUTTON =====

  const hasDropdown = useMemo(() => {
    return topMenu?.find((item) => item.type === "dropdown");
  }, [topMenu]);
  const renderHeader = () => (
    <div
      style={{
        minHeight: hasDropdown ? "100px" : "54px",
        alignItems: "center",
        flexShrink: 0,
      }}
    >
      {/* Logo */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "32px",
          justifyContent: collapsed ? "center" : "flex-start",
        }}
      >
        <img
          src={"/testnimbus-logo.png"}
          alt="TestNimbus Logo"
          style={{
            height: "32px",
            width: "auto",
            objectFit: "contain",
          }}
        />
        {!collapsed && (
          <h6
            style={{
              fontSize: "16px",
              fontWeight: 700,
              color: SIDEBAR_LABEL_COLOR,
              marginLeft: "12px",
              margin: 0,
            }}
          >
            TestNimbus
          </h6>
        )}
      </div>

      {/* Project Selector - Only show when not collapsed and in project context */}
      {!collapsed && inProjectContext && (
        <div style={{ marginBottom: "24px", marginTop: "24px" }}>
          {topMenu.map((item) => {
            if (item.type === "dropdown") {
              return (
                <Select
                  key={item.label}
                  value={searchParams.get("project_id") || ""}
                  onValueChange={(value) => {
                    const project = projects.find(
                      (p) => p.project_id === value,
                    );
                    item.onClick(value, project?.name);
                  }}
                >
                  <SelectTrigger
                    style={{
                      height: "30px",
                      borderRadius: "8px",
                      marginTop: "22px",
                      padding: "0 12px",
                      width: "100%",
                      color: SIDEBAR_LABEL_COLOR,
                    }}
                  >
                    <SelectValue placeholder={item.label} />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.project_id} value={p.project_id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );

  const menuItemStyle = (active) => ({
    height: "30px",
    borderRadius: "10px",
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: active ? 700 : 500,
    backgroundColor: active ? "#F5F4F4FF" : "transparent",
    color: SIDEBAR_LABEL_COLOR,
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s",
    width: collapsed ? "auto" : "100%",
    justifyContent: collapsed ? "center" : "flex-start",
  });

  /** Long labels wrap; hover/active background stays inside rounded corners */
  const multilineNavRowOverrides = {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    height: "auto",
    minHeight: "30px",
    alignItems: "flex-start",
    alignSelf: "stretch",
    padding: "6px 8px",
    overflow: "hidden",
  };

  const multilineNavLabelSpan = {
    flex: 1,
    minWidth: 0,
    maxWidth: "100%",
    textAlign: "left",
    lineHeight: 1.35,
    overflowWrap: "break-word",
    wordBreak: "break-word",
  };

  const multilineNestedIconAdjust = { marginTop: "2px" };

  return (
    <div className="flex relative sidebar">
      <aside
        className="fixed left-0 top-0 h-screen transition-all flex flex-col z-40 sidebar-aside"
        style={{
          width: drawerWidth,
          paddingTop: "16px",
          paddingBottom: "16px",
          paddingLeft: "12px",
          paddingRight: "12px",
        }}
      >
        {renderHeader()}

        {/* Top Menu */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            minWidth: 0,
            paddingTop: collapsed ? "8px" : "0",
            gap: "4px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {topMenu.map((item, itemIndex) => {
            if (item.type === "dropdown") {
              // Dropdown handled in header
              return null;
            }

            const IconComponent = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedMenus[item.label];

            const settingsSectionNorm = searchParams.get("section");

            const isAnyChildActive =
              hasChildren &&
              item.children.some((child) => {
                if (child.id != null && child.path.startsWith("/settings")) {
                  return (
                    location.pathname.startsWith("/settings") &&
                    (settingsSectionNorm === child.id ||
                      (!settingsSectionNorm && child.id === "details"))
                  );
                }
                return (
                  child.path &&
                  isSidebarChildPathActive(child, location.pathname)
                );
              });

            const isActiveItem =
              item.settingsSection != null
                ? location.pathname.startsWith("/settings") &&
                  (settingsSectionNorm === item.settingsSection ||
                    (!settingsSectionNorm &&
                      item.settingsSection === "details"))
                : onSettingsPage && item.id
                  ? settingsSectionNorm === item.id ||
                    (!settingsSectionNorm && item.id === "details")
                  : inProjectContext
                    ? isAnyChildActive ||
                      Boolean(
                        item.path &&
                        (item.path === "/test-cases/history"
                          ? isSidebarChildPathActive(
                              { path: "/test-cases/history" },
                              location.pathname,
                            )
                          : item.path === "/test-cases"
                            ? isSidebarChildPathActive(
                                { path: "/test-cases" },
                                location.pathname,
                              )
                            : location.pathname.startsWith(item.path)),
                      )
                    : isActive(item) || isAnyChildActive;

            const isPresentationDropdown =
              item.navPresentation === "dropdown" &&
              hasChildren &&
              IconComponent;

            if (isPresentationDropdown) {
              const dropdownOpen =
                administrationMenuOpen &&
                item.label === "Administration" &&
                !collapsed;

              return (
                <div
                  key={
                    item.id != null
                      ? `nav-id-${item.id}`
                      : `nav-${item.label}-${itemIndex}`
                  }
                  style={{
                    width: collapsed ? "auto" : "100%",
                    minWidth: collapsed ? undefined : 0,
                    alignSelf: collapsed ? undefined : "stretch",
                  }}
                >
                  <button
                    type="button"
                    aria-expanded={dropdownOpen}
                    title={collapsed ? item.label : undefined}
                    style={{
                      ...menuItemStyle(isActiveItem || isAnyChildActive),
                      ...(collapsed ? {} : { paddingRight: "8px" }),
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (collapsed) return;
                      setAdministrationMenuOpenPersisted((v) => !v);
                    }}
                    onMouseEnter={(e) => {
                      if (!isActiveItem && !isAnyChildActive) {
                        e.currentTarget.style.backgroundColor =
                          "var(--color-bg-hover)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActiveItem && !isAnyChildActive) {
                        e.currentTarget.style.backgroundColor =
                          administrationMenuOpen && !collapsed
                            ? "var(--color-bg-hover)"
                            : "transparent";
                      }
                    }}
                  >
                    <IconComponent
                      style={{
                        width: "18px",
                        height: "18px",
                        flexShrink: 0,
                        color: SIDEBAR_LABEL_COLOR,
                      }}
                    />
                    {!collapsed && (
                      <>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight:
                              isActiveItem || isAnyChildActive ? 700 : 520,
                            color: SIDEBAR_LABEL_COLOR,
                            flex: 1,
                            textAlign: "left",
                          }}
                        >
                          {item.label}
                        </span>
                        <ChevronDown
                          aria-hidden
                          style={{
                            width: "16px",
                            height: "16px",
                            flexShrink: 0,
                            opacity: dropdownOpen ? 1 : 0.85,
                            color: SIDEBAR_LABEL_COLOR,
                            transform: administrationMenuOpen
                              ? "rotate(180deg)"
                              : "rotate(0deg)",
                            transition:
                              "transform 0.2s ease, opacity 0.15s ease",
                          }}
                        />
                      </>
                    )}
                  </button>
                  {dropdownOpen && (
                    <div
                      role="menu"
                      aria-label="Administration sections"
                      onMouseDown={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        marginTop: "6px",
                        marginBottom: "4px",
                        padding: "6px",
                        borderRadius: "10px",
                        backgroundColor: "var(--color-bg-gray)",
                        border: "1px solid var(--color-border-primary)",
                        boxShadow: "none",
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        minWidth: 0,
                        width: "100%",
                      }}
                    >
                      {item.children.map((child) => {
                        if (
                          child.id == null ||
                          !child.path.startsWith("/settings")
                        ) {
                          return null;
                        }
                        const ChildIco = child.icon;
                        const isChildDdActive =
                          location.pathname.startsWith("/settings") &&
                          (settingsSectionNorm === child.id ||
                            (!settingsSectionNorm && child.id === "details"));

                        return (
                          <button
                            key={child.id}
                            type="button"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            onPointerDown={(e) => {
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              persistAdministrationMenuSession(true);
                              setAdministrationMenuOpen(true);
                              navigateToClientSettingsSection(child.id);
                            }}
                            style={{
                              ...menuItemStyle(isChildDdActive),
                              ...multilineNavRowOverrides,
                              fontSize: "12px",
                              lineHeight: 1.3,
                              textAlign: "left",
                              marginBottom: 0,
                            }}
                            onMouseEnter={(e) => {
                              if (!isChildDdActive) {
                                e.currentTarget.style.backgroundColor =
                                  "var(--color-bg-hover)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isChildDdActive) {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                              } else {
                                e.currentTarget.style.backgroundColor =
                                  "#F5F4F4FF";
                              }
                            }}
                          >
                            <ChildIco
                              style={{
                                width: "16px",
                                height: "16px",
                                flexShrink: 0,
                                color: isChildDdActive
                                  ? ADMIN_NAV_SELECTED.color
                                  : SIDEBAR_LABEL_COLOR,
                                ...multilineNestedIconAdjust,
                              }}
                            />
                            <span
                              style={{
                                ...multilineNavLabelSpan,
                                fontSize: "12px",
                                ...(isChildDdActive
                                  ? ADMIN_NAV_SELECTED
                                  : {
                                      fontWeight: 500,
                                      color: SIDEBAR_LABEL_COLOR,
                                    }),
                              }}
                            >
                              {child.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const handleClick = (e) => {
              e.preventDefault();
              e.stopPropagation();

              if (
                hasChildren &&
                !collapsed &&
                item.navPresentation !== "dropdown"
              ) {
                const firstChild = item.children[0];
                if (!isExpanded) toggleMenuExpansion(item.label);
                if (firstChild?.id != null && firstChild.path === "/settings") {
                  navigateToClientSettingsSection(firstChild.id);
                } else if (firstChild?.path) {
                  const tabKey = resolveTabKeyFromSidebarChild(firstChild);
                  if (workspaceTabs && tabKey) {
                    workspaceTabs.openWorkspaceTab(tabKey);
                  } else if (inProjectContext) {
                    const currentParams = new URLSearchParams(location.search);
                    navigate(firstChild.path + "?" + currentParams.toString());
                  } else {
                    navigate(firstChild.path);
                  }
                }
                return;
              }

              if (item.path === "/projects/add") {
                const currentParams = new URLSearchParams(location.search);
                currentParams.set("action", "create");
                navigate(`/projects?${currentParams.toString()}`);
              } else if (
                item.path === "/migration" &&
                (onProjectsPage || onMigrationPage)
              ) {
                const cid =
                  searchParams.get("client_id") ||
                  user?.client_id ||
                  getStoredClientId() ||
                  "";
                navigate(`/migration?client_id=${cid}`);
              } else if (item.settingsSection != null) {
                navigateToClientSettingsSection(item.settingsSection);
              } else if (onSettingsPage && item.id) {
                navigateToClientSettingsSection(item.id);
              } else if (item.path) {
                const tabKey = resolveTabKeyFromSidebarItem(item);
                if (workspaceTabs && tabKey) {
                  workspaceTabs.openWorkspaceTab(tabKey);
                } else if (inProjectContext) {
                  const currentParams = new URLSearchParams(location.search);
                  if (item.path === "/test-cases/history") {
                    const lastId = getLastTestCaseForHistory();
                    if (lastId && currentParams.get("project_id")) {
                      navigate(
                        `/test-cases/history/${lastId}?${currentParams.toString()}`,
                      );
                    } else {
                      navigate(
                        `/test-cases/history?${currentParams.toString()}`,
                      );
                    }
                  } else {
                    const newPath = item.path + "?" + currentParams.toString();
                    navigate(newPath);
                  }
                } else {
                  if (item.path.startsWith("/my-account")) {
                    sessionStorage.setItem(
                      "myAccountFrom",
                      location.pathname + location.search,
                    );
                  }
                  navigate(item.path);
                }
              }
            };

            const navLabelTint = item.navLabelColor ?? SIDEBAR_LABEL_COLOR;
            const navIconTint = item.navIconColor ?? navLabelTint;

            return (
              <div
                key={
                  item.id != null
                    ? `nav-id-${item.id}`
                    : `nav-${item.label}-${itemIndex}`
                }
                style={{ width: "100%", minWidth: 0 }}
              >
                <Tooltip content={item.label} disabled={!collapsed}>
                  <button
                    type="button"
                    onClick={handleClick}
                    style={{
                      ...menuItemStyle(isActiveItem || isAnyChildActive),
                      ...(onSettingsPage && item.id
                        ? multilineNavRowOverrides
                        : {}),
                      ...(item.navLabelColor ? { color: navLabelTint } : {}),
                    }}
                    onMouseEnter={(e) => {
                      if (!isActiveItem && !isAnyChildActive) {
                        e.currentTarget.style.backgroundColor =
                          "var(--color-bg-hover)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActiveItem && !isAnyChildActive) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <IconComponent
                      style={{
                        width: "18px",
                        height: "18px",
                        flexShrink: 0,
                        color: navIconTint,
                        ...(onSettingsPage && item.id
                          ? multilineNestedIconAdjust
                          : {}),
                      }}
                    />
                    {!collapsed && (
                      <>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight:
                              isActiveItem || isAnyChildActive ? 700 : 520,
                            color: navLabelTint,
                            ...(onSettingsPage && item.id
                              ? multilineNavLabelSpan
                              : {
                                  flex: 1,
                                  textAlign: "left",
                                }),
                          }}
                        >
                          {item.label}
                        </span>
                        {hasChildren && item.navPresentation !== "dropdown" && (
                          <ChevronDown
                            style={{
                              width: "16px",
                              height: "16px",
                              flexShrink: 0,
                              color: navLabelTint,
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s",
                            }}
                          />
                        )}
                      </>
                    )}
                  </button>
                </Tooltip>

                {/* Render children if expanded and not collapsed */}
                {hasChildren &&
                  item.navPresentation !== "dropdown" &&
                  isExpanded &&
                  !collapsed && (
                    <div
                      style={{
                        paddingLeft: "24px",
                        marginTop: "2px",
                        minWidth: 0,
                        width: "100%",
                      }}
                    >
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const isChildActive =
                          child.id != null && child.path === "/settings"
                            ? location.pathname.startsWith("/settings") &&
                              (settingsSectionNorm === child.id ||
                                (!settingsSectionNorm &&
                                  child.id === "details"))
                            : inProjectContext
                              ? Boolean(
                                  child.path &&
                                  isSidebarChildPathActive(
                                    child,
                                    location.pathname,
                                  ),
                                )
                              : isActive(child);

                        const handleChildClick = (e) => {
                          e.preventDefault();
                          e.stopPropagation();

                          if (child.id != null && child.path === "/settings") {
                            navigateToClientSettingsSection(child.id);
                            return;
                          }

                          if (inProjectContext && child.path) {
                            const currentParams = new URLSearchParams(
                              location.search,
                            );
                            if (child.historyTab === "test_step") {
                              currentParams.set("tab", "test_step");
                              navigate(
                                `/test-cases/history?${currentParams.toString()}`,
                              );
                            } else if (child.path === "/test-cases/history") {
                              currentParams.delete("tab");
                              const lastId = getLastTestCaseForHistory();
                              if (lastId && currentParams.get("project_id")) {
                                navigate(
                                  `/test-cases/history/${lastId}?${currentParams.toString()}`,
                                );
                              } else {
                                navigate(
                                  `/test-cases/history?${currentParams.toString()}`,
                                );
                              }
                            } else {
                              navigate(
                                child.path + "?" + currentParams.toString(),
                              );
                            }
                          } else if (child.path) {
                            navigate(child.path);
                          }
                        };

                        return (
                          <button
                            key={child.label}
                            type="button"
                            onClick={handleChildClick}
                            style={{
                              ...menuItemStyle(isChildActive),
                              ...multilineNavRowOverrides,
                              fontSize: "11px",
                              marginBottom: "2px",
                            }}
                            onMouseEnter={(e) => {
                              if (!isChildActive) {
                                e.currentTarget.style.backgroundColor =
                                  "var(--color-bg-hover)";
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isChildActive) {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                              }
                            }}
                          >
                            <ChildIcon
                              style={{
                                width: "16px",
                                height: "16px",
                                flexShrink: 0,
                                color: SIDEBAR_LABEL_COLOR,
                                ...multilineNestedIconAdjust,
                              }}
                            />
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: isChildActive ? 700 : 500,
                                color: SIDEBAR_LABEL_COLOR,
                                ...multilineNavLabelSpan,
                              }}
                            >
                              {child.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
              </div>
            );
          })}
        </div>

        {/* Bottom Menu - Footer Links */}
        <div style={{ marginTop: "auto", paddingTop: "16px" }}>
          <div
            style={{
              // borderTop: '1px solid var(--color-border-primary)',
              paddingTop: "16px",
            }}
          >
            <MyAccount collapsed={collapsed} />
            {bottomMenu.map((item) => {
              const IconComponent = item.icon;
              const handleBottomMenuClick = (e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                }

                if (item.path === "/settings") {
                  navigateToClientSettingsSection("details");
                } else {
                  navigate(item.path, { replace: false });
                }
              };

              const isActiveItem = isActive(item.path);

              return (
                <div
                  key={item.label}
                  style={{ width: collapsed ? "auto" : "100%" }}
                >
                  <Tooltip content={item.label} disabled={!collapsed}>
                    <button
                      type="button"
                      onClick={handleBottomMenuClick}
                      style={{
                        ...menuItemStyle(isActiveItem),
                        fontSize: "13px",
                        marginBottom: item.captionBelow ? "0" : "4px",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActiveItem) {
                          e.currentTarget.style.backgroundColor =
                            "var(--color-bg-hover)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActiveItem) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <IconComponent
                        style={{
                          width: "18px",
                          height: "18px",
                          flexShrink: 0,
                          color: SIDEBAR_LABEL_COLOR,
                        }}
                      />
                      {!collapsed && (
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: isActiveItem ? 700 : 500,
                            color: SIDEBAR_LABEL_COLOR,
                          }}
                        >
                          {item.label}
                        </span>
                      )}
                    </button>
                  </Tooltip>
                  {!collapsed && item.captionBelow ? (
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: SIDEBAR_LABEL_COLOR,
                        paddingLeft: "6px",
                        marginTop: "12px",
                        marginBottom: "4px",
                        lineHeight: 1.3,
                      }}
                    >
                      {item.captionBelow}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </aside>

      {/* Collapse Button */}
      <Button
        onClick={handleCollapseToggle}
        variant="outline"
        size="icon"
        className="fixed top-5 h-8 w-8 p-0 transition-all shadow-md collapse-btn"
        style={{
          // Position button at the right edge of sidebar, centered on the edge
          // When collapsed: sidebar is 55px wide; when expanded: 210px
          left: collapsed ? "62px" : "190px",
          top: "16px",
          transform: "translateX(-50%)",
          // Lower z-index when modal is open to appear behind modal backdrop (z-100)
          zIndex: isModalOpen ? 30 : 9999,
          backgroundColor: "var(--color-bg-primary)",
          borderColor: "var(--color-border-primary)",
          color: "var(--color-text-primary)",
          // Disable pointer events when modal is open
          pointerEvents: isModalOpen ? "none" : "auto",
        }}
        aria-hidden={isModalOpen}
        tabIndex={isModalOpen ? -1 : 0}
      >
        {collapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </Button>

      {/* Spacer to prevent content overlap */}
      <div style={{ width: drawerWidth }} />
    </div>
  );
};

// Simple Tooltip component
const Tooltip = ({ children, content, disabled }) => {
  if (disabled) return children;

  return (
    <div className="group relative w-full">
      {children}
      <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity">
        {content}
      </div>
    </div>
  );
};

export default Sidebar;
